/* eslint-disable @typescript-eslint/return-await */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Tab, Typography } from '@mui/material'
import { TabContext, TabList } from '@mui/lab'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DivisionManager } from '../../../../APIManager'
import ROUTES from '../../../../consts/paths'
import { type Row } from '../../../../components/CustomTable/types'
import LeadText from '../../../../components/CustomTable/CellTypes/LeadText'
import { renderCrossListedInfo, renderInstructorDetails, renderIsReportReleased, renderModifiedOn, renderSurveyScore } from './TableColumnsCell'
import { type TableCell, type ExtendedParams } from '../../../../models/CompletionRate'
import { grey } from '../../../../colors'
import { type SurveyCompletionRate } from '../../../../models/Division'
import AppTable from '../../../../components/CustomTable'
import { renderSurveyScoreHeader, renderInstructorSurveyScoreHeader } from '../SurveyScoreInfo'
import { useUserRole } from '../../../../hooks/useUserRole'
import { ROLES } from '../../../../consts/roles'

const INSTRUCTOR_TAB = 'Instructor'

interface SectionTableFeatureProps {
  parentSurveyScore?: number | null
  completionRate?: SurveyCompletionRate | null
  setSelectTab?: React.Dispatch<React.SetStateAction<string>>
  fetchSectionTableData?: (params: ExtendedParams) => Promise<any>
  /** categoryName of the objectId scope division, pre-derived from topBarDivisions. When provided, skips the divisionForTabs completion-rate fetch. */
  objectCategoryName?: string
}

export const SectionTableFeature: React.FC<SectionTableFeatureProps> = ({
  parentSurveyScore,
  completionRate,
  setSelectTab,
  fetchSectionTableData,
  objectCategoryName
}) => {
  const navigate = useNavigate()
  const { activeRole } = useUserRole()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId') ?? undefined
  const distributionSeriesId = searchParams.get('projectId') ?? undefined
  const objectId = searchParams.get('objectId') ?? undefined
  const categoryName = completionRate?.categoryName
  const [preparedTabs, setPreparedTabs] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState<string>('')
  const isFirstMountRef = useRef(true)
  const isFirstTermProjectMountRef = useRef(true)
  const [params, setParams] = useState<ExtendedParams>({
    termId,
    distributionSeriesId,
    pageSize: 5,
    search: ''
  })

  const effectiveDivisionId = (fetchSectionTableData != null && objectId != null) ? objectId : id
  const effectiveCategoryName = (fetchSectionTableData != null && objectId != null)
    ? objectCategoryName
    : categoryName

  const { data: childDivisionsTabs, isLoading: isLoadingChildDivisionsTabs } = useQuery({
    queryKey: ['childDivisionsTabs', effectiveCategoryName, effectiveDivisionId],
    queryFn: async () => DivisionManager.getCategoryTabsChildren(effectiveCategoryName ?? ''),
    select: (data) => data ?? [],
    // Never call academic unit category methods in instructor mode — tabs are hardcoded to ['Section']
    enabled: fetchSectionTableData == null && !!effectiveDivisionId && !!effectiveCategoryName
  })

  // In instructor mode the Section tab always shows the instructor's own sections via
  // fetchSectionTableData, regardless of whether objectId (parent division) is resolved.
  const { data: instructorSectionCDG, isLoading: isLoadingInstructorSection } = useQuery({
    queryKey: ['instructorSections', termId, distributionSeriesId, params],
    queryFn: async () => {
      const fn = fetchSectionTableData
      if (fn == null) return null
      return await fn(params)
    },
    enabled: fetchSectionTableData != null && selectedTab === 'Section' && !!termId && !!distributionSeriesId
  })

  const isSectionTableQueryEnabled = !!effectiveDivisionId && !!termId && !!distributionSeriesId && !!selectedTab && selectedTab !== INSTRUCTOR_TAB && fetchSectionTableData == null
  const { data: CDG, isLoading: isLoadingCDG } = useQuery({
    queryKey: ['completionRatesChildren', effectiveDivisionId, termId, distributionSeriesId, params, selectedTab],
    queryFn: async () => DivisionManager.getSectionTable(effectiveDivisionId ?? '', selectedTab, params),
    enabled: isSectionTableQueryEnabled
  })

  // In instructor mode, show the parent division's instructor list (same CDG as the division page).
  // In division mode, show the current division's instructor list.
  // effectiveDivisionId already resolves to objectId in instructor mode, so reuse it.
  const { data: instructorCDG, isLoading: isLoadingInstructors } = useQuery({
    queryKey: ['instructorCompletionRates', effectiveDivisionId, termId, distributionSeriesId, params],
    queryFn: async () => DivisionManager.getInstructorCompletionRates(effectiveDivisionId ?? '', params),
    enabled: selectedTab === INSTRUCTOR_TAB && !!effectiveDivisionId && !!termId && !!distributionSeriesId && (fetchSectionTableData == null || objectId != null)
  })

  const isAccountOwner = activeRole?.roleName === ROLES.ACCOUNT_OWNER

  // No freshness hint here any more. It used to warn Account Owner that these counts were live
  // while the full report still showed the last scheduled release — true while the list had its own
  // always-instant generators. Backend CR-271 Behavior Change #8 deleted those: the list row and
  // the report now read the same `section_term_summary_report_index` row, refreshed together, so
  // the discrepancy the hint described no longer exists. The "Last updated" column is what tells
  // the admin how current the shared row is.

  const tabsWithSection = useMemo(() => {
    return (childDivisionsTabs ?? []).map((c: { categoryName: string }) => c.categoryName)
  }, [childDivisionsTabs])

  useEffect(() => {
    if (fetchSectionTableData != null) {
      // Instructor view always shows only the Section tab.
      setPreparedTabs(['Section'])
      setSelectedTab(prev => prev === '' ? 'Section' : prev)
      return
    }
    if (isLoadingChildDivisionsTabs) return

    const prepared = [...tabsWithSection, 'Section', INSTRUCTOR_TAB]

    setPreparedTabs(prepared)
    setSelectedTab(prepared[0] ?? '')
  }, [isLoadingChildDivisionsTabs, tabsWithSection, fetchSectionTableData])

  // Reset tab state when navigating to a different instructor (same component instance, new id param).
  // Skip on initial mount so the tab init effect's selection is not cleared.
  useEffect(() => {
    if (isFirstMountRef.current) {
      isFirstMountRef.current = false
      return
    }
    setSelectedTab('')
    setParams({ termId, distributionSeriesId, pageSize: 5, search: '' })
  }, [id])

  // Reset params when the survey project or term changes without a division navigation.
  // This keeps params.distributionSeriesId / params.termId in sync with the URL so the
  // correct data is fetched after the user picks a different project/term and clicks Go.
  useEffect(() => {
    if (isFirstTermProjectMountRef.current) {
      isFirstTermProjectMountRef.current = false
      return
    }
    setParams({ termId, distributionSeriesId, pageSize: 5, search: '' })
  }, [termId, distributionSeriesId])

  // Guard: if preparedTabs changes and the current selectedTab is not in the list, select the first tab.
  // This covers timing edge cases where selectedTab gets out of sync with the available tabs.
  useEffect(() => {
    if (preparedTabs.length > 0 && !preparedTabs.includes(selectedTab)) {
      setSelectedTab(preparedTabs[0])
    }
  }, [preparedTabs, selectedTab])

  // this useEffect is for showing the export modal condition for showing if we have the tab === Section :
  useEffect(() => {
    setSelectTab?.(selectedTab ?? '')
  }, [selectedTab])

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
    setParams({ termId, distributionSeriesId, pageSize: 5, search: '' })
  }, [termId, distributionSeriesId])

  const handleCellClick = useCallback((row: Row) => {
    const rowData = row.row.data
    const division = rowData.divisionId ?? rowData.sectionId
    const sp = new URLSearchParams(searchParams)
    sp.delete('tablePageNumber')
    if (typeof rowData.slotStatus === 'string' && rowData.slotStatus.length > 0) {
      sp.set('slotStatus', rowData.slotStatus)
    } else {
      sp.delete('slotStatus')
    }
    // Preserve instructor context so the section report page can show the view toggle back.
    if (fetchSectionTableData != null && id) {
      sp.set('fromInstructorId', id)
    }
    navigate(`${ROUTES.REPORTS}/${division}?${sp.toString()}`)
  }, [navigate, searchParams, fetchSectionTableData, id])

  const handleInstructorRowClick = useCallback((row: Row) => {
    // row may be GridRowParams (from DataGrid onRowClick, data at row.row.data)
    // or flattenedData (from cell override onClick, data at top level)
    const data: Record<string, any> = row?.row?.data ?? row
    // CR-321: the Backend sets `canViewReport` with the same check the instructor report uses, so
    // `false` means that report would refuse this viewer. A missing flag keeps the row openable.
    if (data.canViewReport === false) return
    const userId = data.userId
    // In instructor mode (fetchSectionTableData provided), `id` is the instructor's
    // user UUID — not a division UUID. Only use objectId as the division scope.
    const divisionId = fetchSectionTableData != null ? (objectId ?? '') : (objectId ?? id ?? '')
    const objectIdParam = divisionId ? `&objectId=${divisionId}` : ''
    navigate(
      `${ROUTES.INSTRUCTOR_SURVEYS}/${userId}?view=report&termId=${termId ?? ''}&projectId=${distributionSeriesId ?? ''}${objectIdParam}`
    )
  }, [navigate, termId, distributionSeriesId, id, objectId])

  const instructorOverrides = useMemo(() => ({
    categoryName: (cell: TableCell) => (
      cell.canViewReport === false
        ? <Typography sx={{ fontSize: '14px', color: grey[850], fontWeight: 400 }}>
            {cell.categoryName}
          </Typography>
        : <LeadText onClick={() => { handleInstructorRowClick(cell) }}>
            {cell.categoryName}
          </LeadText>
    ),
    completionPercent: (cell: TableCell) => (
      !cell?.isAnonymousSurvey
        ? <Typography sx={{ fontSize: '14px', color: grey[850], fontWeight: 400 }}>
            {Math.round(cell.completionPercent ?? 0)}%
          </Typography>
        : <Typography sx={{ fontSize: '14px', color: grey[850], fontWeight: 400 }}>
            {cell?.completionCount}
          </Typography>
    ),
    surveyScore: (cell: TableCell) => renderSurveyScore(cell, parentSurveyScore)
  }), [handleInstructorRowClick, parentSurveyScore])

  // The cross-listed info icon is instructor-dashboard only (fetchSectionTableData provided),
  // where the BE stamps `isCrosslistedSection` on cross-listed Course Sections.
  const isInstructorDashboard = fetchSectionTableData != null

  const overrides = useMemo(() => ({
    categoryName: (cell: TableCell) => (
      <LeadText onClick={() => { handleCellClick(cell) }}>
        {cell.categoryName}
      </LeadText>
    ),
    // On the instructor dashboard the leading column is the Section ID (`abbreviation`),
    // not `categoryName`, so the cross-listed info icon is appended here. The Link mirrors
    // the default LINK cell so navigation is unchanged. This override is only added in
    // instructor mode; division mode keeps the default `abbreviation` cell rendering.
    ...(isInstructorDashboard
      ? {
          abbreviation: (cell: TableCell) => {
            const label = typeof cell.abbreviation === 'string' ? cell.abbreviation : ''
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Link
                  to={`${ROUTES.REPORTING_HIERARCHY}/${cell.divisionId ?? cell.sectionId ?? ''}`}
                  style={{ textDecoration: 'none', cursor: 'pointer' }}
                >
                  <LeadText sx={{ whiteSpace: 'pre' }} title={label}>{label}</LeadText>
                </Link>
                {cell.isCrosslistedSection === true && renderCrossListedInfo()}
              </Box>
            )
          }
        }
      : {}),
    completionPercent: (cell: TableCell) => (
      !cell?.isAnonymousSurvey
        ? <Typography sx={{ fontSize: '14px', color: grey[850], fontWeight: 400 }}>
            {Math.round(cell.completionPercent ?? 0)}%
          </Typography>
        : <Typography sx={{ fontSize: '14px', color: grey[850], fontWeight: 400 }}>
            {cell?.completionCount}
          </Typography>
    ),
    surveyScore: (cell: TableCell) => renderSurveyScore(cell, parentSurveyScore),
    instructorDetails: renderInstructorDetails,
    isReportReleased: renderIsReportReleased,
    modifiedOn: renderModifiedOn
  }), [handleCellClick, parentSurveyScore, isInstructorDashboard])

  // Adds an info icon + tooltip beside the Survey Score column header. On the instructor
  // reports page (fetchSectionTableData provided) the score is scoped to one instructor +
  // section, so it uses the instructor-specific copy.
  const headerOverrides = useMemo(() => ({
    surveyScore: fetchSectionTableData != null ? renderInstructorSurveyScoreHeader : renderSurveyScoreHeader
  }), [fetchSectionTableData])

  const isInstructorTab = selectedTab === INSTRUCTOR_TAB

  const list = isInstructorTab
    ? instructorCDG?.data
    : (fetchSectionTableData != null
        ? instructorSectionCDG?.data
        : CDG?.data)

  // CR-271 Behavior Change #9: the Backend adds `modifiedOn` to the Section-children `keys`
  // envelope for every role, not just Account Owner — so FE must filter the column out for
  // everyone else rather than relying on the Backend to scope it. No-op today (a no-op filter
  // over an array that doesn't have this key yet) until the Backend actually ships the field.
  const columns = useMemo(() => {
    const keys = list?.keys
    if (!Array.isArray(keys)) return undefined
    const showModifiedOnColumn = isAccountOwner && selectedTab === 'Section'
    return showModifiedOnColumn ? keys : keys.filter((key) => key.field !== 'modifiedOn')
  }, [list, isAccountOwner, selectedTab])

  const handleRowClick = useCallback((row: Row) => {
    if (isInstructorTab) {
      handleInstructorRowClick(row)
    } else {
      handleCellClick(row)
    }
  }, [isInstructorTab, handleInstructorRowClick, handleCellClick])

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      <TabContext value={selectedTab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', display: fetchSectionTableData != null ? 'none' : undefined }}>
          <TabList
            onChange={handleTabChange}
            aria-label="category-tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                fontSize: '16px',
                fontWeight: 600
              }
            }}
          >
            {preparedTabs.map((tab) => (
              <Tab key={tab} label={tab} value={tab} />
            ))}
          </TabList>
        </Box>
      </TabContext>
      <AppTable
        loading={
          isInstructorTab
            ? isLoadingInstructors
            : (fetchSectionTableData != null
                ? isLoadingInstructorSection
                : (isLoadingCDG || isLoadingChildDivisionsTabs))
        }
        featureName="Completion Rates"
        params={params}
        setParams={setParams}
        list={list}
        columns={columns}
        onRowClick={handleRowClick}
        overrides={isInstructorTab ? instructorOverrides : overrides}
        headerOverrides={headerOverrides}
      />
    </Box>
  )
}
